/**
 * Coups de pouce — Attendu 1117 « Connaître et utiliser les formules du volume
 * d'un cube, d'un pavé, d'un prisme, d'un cylindre » (automatisme 4e).
 *
 * calcul_* (saisie, QCM) et vf_tableau_vf : entrée PARTAGÉE — une seule idée
 * (aire de la base × hauteur) déclinée sur les quatre solides, le ÷ 2 du
 * triangle, π laissé dans le résultat, l'unité. L'exemple (revue 2026-09-09) :
 * prisme droit à base triangle rectangle 5 × 4, hauteur 6 → 10 × 6 = 60 cm³,
 * piège 5 × 4 × 6. SVG piloté par les flèches de la sheet.
 *
 * dimension_saisie : la hauteur comme multiplication à trou, aire de la base
 * × … = volume, donc volume ÷ aire de la base. L'exemple : pavé de 72 cm³ et
 * de base 6 × 2 → 12 × ? = 72 → 6 cm, piège 72 − 12.
 *
 * formule_* (QCM, relier, V/F) : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */

const SEG = (a, b, dash = false) => `<line x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}" stroke="#1f2937" stroke-width="1.7" stroke-linecap="round"${dash ? ' stroke-dasharray="5 4"' : ''}/>`;
const COTE = (x, y, txt, couleur = '#b45309', anchor = 'middle') => `<text x="${x}" y="${y}" font-family="system-ui,sans-serif" font-size="11.5" font-weight="700" fill="${couleur}" text-anchor="${anchor}">${txt}</text>`;

// ─── Prisme droit à base triangle rectangle 5 × 4, hauteur 6 (cavalière précalculée) ─
// triangle avant : f0 (angle droit) en bas à gauche, f1 à droite, f2 en haut ; fuyante (+52, −30)
const f0 = [40, 200]; const f1 = [150, 200]; const f2 = [40, 112]; const b0 = [92, 170]; const b1 = [202, 170]; const b2 = [92, 82];
const PRISME = SEG(b0, b1, true) + SEG(f0, b0, true) + SEG(b0, b2, true)
    + SEG(f0, f1) + SEG(f1, f2) + SEG(f2, f0) + SEG(b1, b2) + SEG(f1, b1) + SEG(f2, b2)
    + `<path d="M ${f0[0] + 9} ${f0[1]} L ${f0[0] + 9} ${f0[1] - 9} L ${f0[0]} ${f0[1] - 9}" fill="none" stroke="#1f2937" stroke-width="1.2"/>`
    + COTE(95, 216, '5 cm') + COTE(30, 160, '4 cm', '#b45309', 'end') + COTE(194, 194, '6 cm');

const SVG_CALCUL = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 300" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : quel est le volume d'un prisme droit à base triangle rectangle de côtés 5 et 4 centimètres et de hauteur 6 centimètres ? En 3 étapes à faire défiler avec les flèches. Étape 1 : la base est le triangle de devant, la hauteur est la longueur de 6 centimètres qui va vers l'arrière. Étape 2 : aire de la base, 5 fois 4 divisé par 2 égale 10 centimètres carrés. Étape 3 : volume, 10 fois 6 égale 60 centimètres cubes ; piège : 5 fois 4 fois 6 égale 120 est faux, le divisé par 2 est oublié. Réponse : le volume est 60 centimètres cubes">
  <style>
    @keyframes cp1117-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1117-pop { opacity: 0; animation: cp1117-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Repère la base et la hauteur</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">L'aire de la base, avec le ÷ 2 du triangle</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Volume = aire de la base × hauteur</text>

  <!-- ── La figure, base et hauteur (étape 1) ────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Prisme droit : base triangle rectangle 5 cm et 4 cm, hauteur 6 cm.</text>
    <text x="8" y="60" font-size="11" font-weight="700" fill="#1E3A8A">Question : quel est le volume de ce prisme ?</text>
    ${PRISME}
    <text x="222" y="120" font-size="11" font-weight="700" fill="#1E3A8A">La base : le triangle</text>
    <text x="222" y="134" font-size="11" font-weight="700" fill="#1E3A8A">de devant.</text>
    <text x="222" y="156" font-size="11" font-weight="700" fill="#1E3A8A">La hauteur : 6 cm,</text>
    <text x="222" y="170" font-size="11" font-weight="700" fill="#1E3A8A">d'une base à l'autre.</text>
  </g>

  <!-- ── L'aire de la base (étape 2) ─────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1117-pop">
      <polygon points="${f0[0]},${f0[1]} ${f1[0]},${f1[1]} ${f2[0]},${f2[1]}" fill="#1D9E75" fill-opacity="0.25"/>
      <text x="8" y="240" font-size="11.5" font-weight="700" fill="#1D9E75">Aire de la base = (5 × 4) ÷ 2 = 20 ÷ 2 = 10 cm²</text>
    </g>
  </g>

  <!-- ── Le volume et le piège (étape 3) ─────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1117-pop">
      <text x="8" y="260" font-size="12" font-weight="800" fill="#B45309">Volume = 10 × 6 = 60 cm³ ✓</text>
      <text x="8" y="276" font-size="11" font-weight="700" fill="#DC2626">5 × 4 × 6 = 120 ✗  le ÷ 2 du triangle est oublié</text>
      <text x="8" y="294" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : le volume du prisme est 60 cm³.</text>
    </g>
  </g>
</svg>`.trim();

const AIDE_CALCUL = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Une seule idée pour les quatre solides** : volume = aire de la base × hauteur. La base est une face posée au sol, la hauteur est la longueur qui va de cette base à la face d'en face.",
                '**Pavé et cube** : la base est un rectangle, son aire est L × l, donc V = L × l × h ; pour un cube, les trois arêtes sont égales, V = c × c × c.',
                "**Prisme droit** : la base est le triangle (ou le polygone) des deux bouts. Si c'est un triangle rectangle, son aire est (côté × côté) ÷ 2, n'oublie pas le ÷ 2.",
                '**Cylindre** : la base est un disque, son aire est π × r × r, donc V = π × r × r × h. On laisse π dans le résultat : 3 × 3 × 4 = 36, le volume est 36π cm³.',
                "**Vérifie l'unité** : un volume est en cm³, dm³ ou m³, jamais en cm².",
            ],
        },
        { titre: 'Un exemple', svg: SVG_CALCUL },
    ],
};

// ─── Pavé de 72 cm³, base 6 × 2, hauteur inconnue (cavalière précalculée) ───
const A = [50, 190]; const B = [170, 190]; const E = [50, 110]; const F = [170, 110]; const D = [92, 166]; const C = [212, 166]; const H = [92, 86]; const G = [212, 86];
const PAVE = SEG(D, A, true) + SEG(D, C, true) + SEG(D, H, true)
    + SEG(A, B) + SEG(B, C) + SEG(E, F) + SEG(F, G) + SEG(G, H) + SEG(H, E) + SEG(A, E) + SEG(B, F) + SEG(C, G)
    + COTE(110, 206, '6 cm') + COTE(204, 186, '2 cm') + COTE(40, 154, '?', '#DC2626', 'end');

const SVG_DIMENSION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 300" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : quelle est la hauteur d'un pavé de volume 72 centimètres cubes dont la base mesure 6 centimètres sur 2 centimètres ? En 3 étapes à faire défiler avec les flèches. Étape 1 : aire de la base, 6 fois 2 égale 12 centimètres carrés. Étape 2 : la formule devient une multiplication à trou, 12 fois point d'interrogation égale 72, donc la hauteur est 72 divisé par 12 égale 6 centimètres. Étape 3 : vérification, 12 fois 6 égale 72 ; piège : 72 moins 12 égale 60 est faux. Réponse : la hauteur est 6 centimètres">
  <style>
    @keyframes cp1117-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1117-pop { opacity: 0; animation: cp1117-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Ce qu'on connaît : le volume et la base</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Une multiplication à trou</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On vérifie, et on évite le piège</text>

  <!-- ── La figure et l'aire de la base (étape 1) ────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Pavé droit de volume 72 cm³, dont la base mesure 6 cm sur 2 cm.</text>
    <text x="8" y="60" font-size="11" font-weight="700" fill="#1E3A8A">Question : quelle est la hauteur de ce pavé ?</text>
    ${PAVE}
    <text x="228" y="112" font-size="11" font-weight="700" fill="#1e293b">Volume : 72 cm³</text>
    <text x="228" y="140" font-size="11" font-weight="700" fill="#1E3A8A">Aire de la base :</text>
    <text x="228" y="154" font-size="11" font-weight="700" fill="#1E3A8A">6 × 2 = 12 cm²</text>
  </g>

  <!-- ── La multiplication à trou (étape 2) ──────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1117-pop">
      <text x="8" y="228" font-size="11.5" font-weight="700" fill="#1D9E75">aire de la base × hauteur = volume, donc 12 × ? = 72</text>
      <text x="8" y="246" font-size="12" font-weight="800" fill="#1D9E75">hauteur = 72 ÷ 12 = 6 cm ✓</text>
    </g>
  </g>

  <!-- ── Vérification et piège (étape 3) ─────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1117-pop">
      <text x="8" y="268" font-size="11.5" font-weight="700" fill="#B45309">Vérification : 12 × 6 = 72 ✓</text>
      <text x="8" y="282" font-size="11" font-weight="700" fill="#DC2626">72 − 12 = 60 ✗  on divise, on ne soustrait pas</text>
      <text x="8" y="297" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : la hauteur du pavé est 6 cm.</text>
    </g>
  </g>
</svg>`.trim();

const AIDE_DIMENSION = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Repars de la formule** : volume = aire de la base × hauteur. Quand on connaît le volume et l'aire de la base, la hauteur est ce qui manque dans une multiplication à trou : aire de la base × … = volume.",
                "**Donc on divise** : hauteur = volume ÷ aire de la base. Pour un pavé, l'aire de la base est L × l ; pour un cylindre, c'est π × r × r, et on divise le nombre devant π par r × r.",
                '**Vérifie** en refaisant la multiplication : aire de la base × hauteur trouvée doit redonner le volume.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_DIMENSION },
    ],
};

const CP_1117 = {
    calcul_saisie: AIDE_CALCUL,
    calcul_qcm_unique: AIDE_CALCUL,
    vf_tableau_vf: AIDE_CALCUL,
    dimension_saisie: AIDE_DIMENSION,
};

export default CP_1117;
